import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { get, getTodos } from '../../nucleo/api';
import { dataHora, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import type { Atividade, Oportunidade } from './tipos';
import { CAMINHO_WORKFLOWS, FormTarefa, rascunhoInicial, type Rascunho } from './FormTarefa';
import { dataDaSugestao, sugestoesPara, type Sugestao, type Workflow } from './crm-sugestoes';

export const useWorkflows = () => useQuery({ queryKey: ['pipeline', 'workflows'], queryFn: () => get<{ abrir_proxima: boolean; itens: Workflow[] }>('/pipeline/workflows'), staleTime: 60_000 });

/**
 * CRM no novo desenho, lote 2 (complemento 5): depois de concluir uma tarefa, "Criar próxima tarefa" na mesma negociação, com o
 * resumo das três últimas concluídas (e as atas) e as sugestões dos workflows ligados e da ata. O formulário já vem com a
 * primeira sugestão; clicar noutra preenche de novo. Nada é criado sem a pessoa salvar.
 */
export function ProximaTarefa({ concluida, ata, aoFechar }: { concluida: Atividade; ata: string; aoFechar: () => void }) {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const workflows = useWorkflows();
  const op = useQuery({ queryKey: ['pipeline', 'oportunidade', concluida.oportunidade_id], queryFn: () => get<Oportunidade>(`/pipeline/oportunidades/${concluida.oportunidade_id}`) });
  const anteriores = useQuery({
    queryKey: ['pipeline', 'atividades-da-oportunidade', concluida.oportunidade_id, 'concluidas'],
    queryFn: () => getTodos<Atividade>('/pipeline/atividades', { oportunidade_id: concluida.oportunidade_id, filtro: 'concluidas' }),
  });
  const [escolhida, setEscolhida] = useState<{ n: number; assunto: string } | null>(null);
  if (!workflows.data || !op.data) return null;
  const sugestoes = sugestoesPara(t, { ...concluida, ata }, { funil_id: op.data.funil_id, etapa_nome: op.data.etapa_nome }, workflows.data.itens);
  const dadosDa = (x: Sugestao): Partial<Rascunho> => ({ titulo: x.assunto, tipo: x.tipo, dataHora: dataDaSugestao(x.dias) });
  const primeira = sugestoes[0]!;
  const descricao = t('crm.proxima.descricaoPadrao', { titulo: concluida.titulo });
  const ultimas = (anteriores.data?.itens ?? []).slice(0, 3);
  const motivo = (x: Sugestao) =>
    x.motivo.chave === 'workflow' ? t('crm.proxima.motivoWorkflow', { nome: x.motivo.nome, passo: x.motivo.passo }) : x.motivo.chave === 'ata' ? t('crm.proxima.motivoAta', { palavra: x.motivo.palavra }) : t('crm.proxima.motivoPadrao');
  const atual = escolhida?.assunto ?? primeira.assunto;
  const painel = (
    <section className="flex flex-col gap-3 rounded-lg border border-borda bg-sup-1 p-3" data-painel-proxima="">
      <div>
        <h3 className="text-sm font-semibold">{t('crm.proxima.anteriores')}</h3>
        {ultimas.length ? (
          <ul className="mt-1 flex flex-col gap-2">
            {ultimas.map((a) => (
              <li key={a.id} className="text-sm">
                <p>
                  <span className="font-medium">{textoLivre(a.titulo, a.parceiro_nome)}</span> <span className="numero text-xs text-tinta-3">{a.concluida_em ? dataHora(a.concluida_em) : ''}</span>
                </p>
                {a.ata && <p className="line-clamp-3 text-xs text-tinta-2" title={textoLivre(a.ata, a.parceiro_nome)}>{textoLivre(a.ata, a.parceiro_nome)}</p>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-xs text-tinta-3">{t('crm.proxima.semAnteriores')}</p>
        )}
      </div>
      <div>
        <h3 className="text-sm font-semibold">{t('crm.proxima.sugestoes')}</h3>
        <p className="text-xs text-tinta-3">{t('crm.proxima.sugestoesAjuda')}</p>
        <div className="mt-2 flex flex-col gap-1.5" data-sugestoes="">
          {sugestoes.map((x) => (
            <button
              key={x.assunto}
              type="button"
              aria-pressed={atual === x.assunto}
              data-sugestao={x.assunto}
              onClick={() => setEscolhida((e) => ({ n: (e?.n ?? 0) + 1, assunto: x.assunto }))}
              className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-left text-sm hover:bg-sup-3 aria-pressed:border-acento-forte"
            >
              <span className="font-medium">
                {t(`tipoAtividade.${x.tipo}`)} · {textoLivre(x.assunto, concluida.parceiro_nome)}
              </span>{' '}
              <span className="text-xs text-tinta-3">{t('crm.proxima.emDias', { count: x.dias })}</span>
              <span className="block text-xs text-tinta-3">{motivo(x)}</span>
            </button>
          ))}
        </div>
        {s.pode('pipeline.editar') && (
          <Link to={CAMINHO_WORKFLOWS} className="mt-2 inline-block text-xs font-medium text-acento-forte underline" onClick={aoFechar}>
            {t('crm.proxima.configurar')}
          </Link>
        )}
      </div>
    </section>
  );
  const sugestao = sugestoes.find((x) => x.assunto === atual) ?? primeira;
  return (
    <FormTarefa
      titulo={t('crm.form.proximaTitulo')}
      travarNegociacao
      painel={painel}
      ajuste={escolhida ? { n: escolhida.n, dados: dadosDa(sugestao) } : undefined}
      inicial={rascunhoInicial(s.usuario.id, {
        parceiroId: concluida.parceiro_id,
        parceiroNome: concluida.parceiro_nome,
        oportunidadeId: concluida.oportunidade_id,
        oportunidadeTitulo: concluida.oportunidade_titulo,
        responsaveis: (concluida.responsaveis ?? []).map((r) => r.id).filter(Boolean).length ? (concluida.responsaveis ?? []).map((r) => r.id) : [s.usuario.id],
        descricao,
        ...dadosDa(primeira),
      })}
      aoFechar={aoFechar}
    />
  );
}
